import { useAuth } from './useAuth';
import { useEffectiveAccess } from './useEffectiveAccess';
import { useFeatureRules } from './useFeatureRules';
import { useSettingValue } from './useSystemSettings';
import { useMemo } from 'react';
import { usePreview } from '@/stores/previewStore';
import {
  evaluate,
  buildUserContext,
  AUTH_MESSAGES,
  PREMIUM_MESSAGES,
  type FeatureKey,
  type ContentRef,
  type ActionKey,
  type AccessDecision,
  type UserContext,
  type PreviewContext,
  type DbRulesContext,
  type DbFeatureRule,
  type SystemAccessConfig,
} from '@/lib/access-control';

// ────────────────────────────────────────────────────────────────
// Re-export types for consumers
// ────────────────────────────────────────────────────────────────
export type { FeatureKey, ContentRef, ActionKey, AccessDecision, UserContext, PreviewContext };
export { AUTH_MESSAGES, PREMIUM_MESSAGES };

// ────────────────────────────────────────────────────────────────
// Legacy FeatureGateKey (backward compat for AuthGate/FeatureGate)
// ────────────────────────────────────────────────────────────────
export type FeatureGateKey =
  | 'can_save_favorites'
  | 'can_manage_lists'
  | 'can_submit_visibility'
  | 'can_submit_condition_reports'
  | 'can_create_alerts'
  | 'can_view_full_visibility_history'
  | 'can_view_personalized_recommendations'
  | 'can_report_data_issues'
  | 'can_submit_spot_warnings'
  | 'can_upload_images'
  | 'can_view_advanced_analytics'
  | 'can_use_advanced_filters'
  | 'can_access_trip_planning'
  | 'can_access_beta_features'
  | 'can_manage_poi_content'
  | 'can_moderate_reports'
  | 'can_access_admin';

const LEGACY_ACTION_MAP: Record<FeatureGateKey, ActionKey> = {
  can_save_favorites:                 'favorites.save',
  can_manage_lists:                   'favorites.manage_lists',
  can_submit_visibility:              'visibility_report.create',
  can_submit_condition_reports:       'visibility_report.create',
  can_create_alerts:                  'alert.create',
  can_view_full_visibility_history:   'visibility_report.view_history',
  can_view_personalized_recommendations: 'favorites.save',
  can_report_data_issues:             'spot.report_issue',
  can_submit_spot_warnings:           'spot.submit_warning',
  can_upload_images:                  'image.upload',
  can_view_advanced_analytics:        'visibility_report.view_history',
  can_use_advanced_filters:           'favorites.save',
  can_access_trip_planning:           'favorites.save',
  can_access_beta_features:           'beta_program.join',
  can_manage_poi_content:             'poi.edit',
  can_moderate_reports:               'moderation.review_reports',
  can_access_admin:                   'admin.access',
};

const LEGACY_PREMIUM_KEYS: FeatureGateKey[] = [
  'can_view_advanced_analytics',
  'can_use_advanced_filters',
  'can_access_trip_planning',
];

// ────────────────────────────────────────────────────────────────
// Legacy CTA messages
// ────────────────────────────────────────────────────────────────
export const AUTH_GATE_MESSAGES: Record<string, { de: string; en: string }> = {
  can_save_favorites:             { de: 'Melde dich an, um Lieblingsspots zu speichern und später schnell wiederzufinden.', en: 'Sign in to save your favorite spots and find them again quickly.' },
  can_submit_visibility:          { de: 'Melde dich an, um Conditions mit der Community zu teilen.', en: 'Sign in to share conditions with the community.' },
  can_create_alerts:              { de: 'Melde dich an, um Benachrichtigungen für deine Lieblingsspots zu erhalten.', en: 'Sign in to get notifications for your favorite spots.' },
  can_view_full_visibility_history: { de: 'Melde dich an, um die vollständige Verlaufshistorie zu sehen.', en: 'Sign in to see the full visibility history.' },
  can_report_data_issues:         { de: 'Melde dich an, um Datenfehler zu melden.', en: 'Sign in to report data issues.' },
  can_submit_spot_warnings:       { de: 'Melde dich an, um Warnhinweise einzureichen.', en: 'Sign in to submit warnings.' },
  can_upload_images:              { de: 'Melde dich an, um Fotos hochzuladen.', en: 'Sign in to upload photos.' },
  default:                        { de: 'Melde dich an, um diese Funktion zu nutzen.', en: 'Sign in to use this feature.' },
};

export const PREMIUM_GATE_MESSAGES: Record<string, { de: string; en: string }> = {
  can_view_advanced_analytics:    { de: 'Erweiterte Analysen und Saisontrends sind Teil des Premium-Plans.', en: 'Advanced analytics and seasonal trends are part of the Premium plan.' },
  can_use_advanced_filters:       { de: 'Erweiterte Suchfilter sind Teil des Premium-Plans.', en: 'Advanced search filters are part of the Premium plan.' },
  can_access_trip_planning:       { de: 'Die Tagesplanung ist Teil des Premium-Plans.', en: 'Trip planning is part of the Premium plan.' },
  default:                        { de: 'Diese Funktion ist Teil des Premium-Plans.', en: 'This feature is part of the Premium plan.' },
};

// ────────────────────────────────────────────────────────────────
// Hook: useAccessControl
//
// Single-source consumer: derives all access state from
// useEffectiveAccess (the central RPC-backed read-model).
// No more multi-hook aggregation — one fetch, one truth.
// ────────────────────────────────────────────────────────────────
export function useAccessControl() {
  const { user, loading: authLoading } = useAuth();
  const { access, loading: accessLoading } = useEffectiveAccess();
  const { rulesMap: featureRulesMap, isLoading: rulesLoading } = useFeatureRules();

  const loading = authLoading || (!!user && (accessLoading || rulesLoading));

  // ── Derived state from the single read-model ─────────────
  const roles = useMemo(
    () => (access?.roles ?? []).map((r) => r.role_code),
    [access],
  );
  const entitlementCodes = useMemo(
    () => (access?.entitlements ?? []).map((e) => e.code),
    [access],
  );
  const betaProgramIds = useMemo(
    () => (access?.beta_programs ?? []).map((b) => b.id),
    [access],
  );
  const planCode = access?.subscription?.plan_code ?? null;
  const isFreePlan = !access?.subscription || planCode === 'free';
  const isSuperAdmin = roles.includes('admin') || roles.includes('super_admin');
  const hasRole = (slug: string) => roles.includes(slug);
  const hasEntitlement = (code: string) => entitlementCodes.includes(code);

  const userContext: UserContext = useMemo(() => buildUserContext(
    user?.id ?? null,
    roles,
    entitlementCodes,
    isFreePlan,
    betaProgramIds.length > 0 ? betaProgramIds : undefined,
    access?.verification_status ?? 'unverified',
    access?.account_status ?? 'active',
  ), [user?.id, roles, entitlementCodes, isFreePlan, betaProgramIds, access]);

  const dbCtx: DbRulesContext = useMemo(() => ({
    featureRules: featureRulesMap as Map<string, DbFeatureRule>,
  }), [featureRulesMap]);

  // ── Preview Context ──────────────────────────────────────
  const { preview: previewState } = usePreview();
  const activePreview = previewState.active ? previewState.context : undefined;

  // ── System config (maintenance kill-switch) ─────────────
  // Source of truth: `system_settings.maintenance_mode`. The engine blocks
  // all non-admin access when true; super_admins bypass. Threading it through
  // every evaluate() call here ensures the toggle has REAL effect platform-wide.
  const maintenanceMode = useSettingValue<boolean>('maintenance_mode', false);
  const systemConfig: SystemAccessConfig = useMemo(
    () => ({ maintenanceMode }),
    [maintenanceMode],
  );

  // ── Central evaluate() — full 4-stage pipeline ──────────

  /** Evaluate access with the central engine. Pass debug: true for admin trace. */
  const evaluateAccess = (request: {
    feature?: FeatureKey;
    content?: ContentRef;
    action?: ActionKey;
    contentOwnerId?: string;
    preview?: PreviewContext;
    debug?: boolean;
  }, systemConfigOverride?: SystemAccessConfig): AccessDecision => {
    // Merge session-level preview with per-call preview (per-call wins)
    const effectivePreview = request.preview ?? activePreview;
    return evaluate(
      { user: userContext, ...request, preview: effectivePreview },
      dbCtx,
      systemConfigOverride ?? systemConfig,
    );
  };

  // ── Convenience wrappers ────────────────────────────────

  const canAccessFeature = (feature: FeatureKey): AccessDecision =>
    evaluate({ user: userContext, feature, preview: activePreview }, dbCtx, systemConfig);

  const canAccessContent = (content: ContentRef): AccessDecision =>
    evaluate({ user: userContext, content, preview: activePreview }, dbCtx, systemConfig);

  const canPerformAction = (action: ActionKey, contentOwnerId?: string): AccessDecision =>
    evaluate({ user: userContext, action, contentOwnerId, preview: activePreview }, dbCtx, systemConfig);

  // ── Legacy API (backward compat) ────────────────────────

  const canAccess = (feature: FeatureGateKey): boolean => {
    if (isSuperAdmin) return true;
    // Maintenance kill-switch — non-admin legacy gates close immediately.
    if (maintenanceMode) return false;
    if (user && hasEntitlement(feature)) return true;

    const actionKey = LEGACY_ACTION_MAP[feature];
    if (actionKey) {
      return evaluate({ user: userContext, action: actionKey }, dbCtx, systemConfig).allowed;
    }

    if (feature === 'can_access_admin') return hasRole('admin') || hasRole('spot_admin');
    if (feature === 'can_access_beta_features') return hasRole('admin');
    if (feature === 'can_moderate_reports') return hasRole('admin') || hasRole('spot_admin');
    if (feature === 'can_manage_poi_content') return hasRole('admin') || hasRole('spot_admin');

    return false;
  };

  const getGateType = (feature: FeatureGateKey): 'none' | 'auth' | 'premium' => {
    if (canAccess(feature)) return 'none';
    if (!user) return 'auth';
    if (LEGACY_PREMIUM_KEYS.includes(feature)) return 'premium';
    return 'premium';
  };

  // Convenience flags — preview-aware tier
  const effectiveTier = activePreview?.asTier ?? userContext.tier;

  return {
    tier: effectiveTier,
    loading,
    isGuest: activePreview ? effectiveTier === 'guest' : !user,
    isAuthenticated: activePreview ? effectiveTier !== 'guest' : !!user,
    isFreePlan: activePreview ? effectiveTier === 'free' : isFreePlan,
    isPremium: effectiveTier === 'premium',
    planCode,
    isPreviewActive: previewState.active,
    maintenanceMode,

    // Central engine
    evaluateAccess,

    // Convenience wrappers
    canAccessFeature,
    canAccessContent,
    canPerformAction,
    userContext,
    dbRulesContext: dbCtx,

    // Legacy API
    canAccess,
    getGateType,

    // Role flags
    isBetaTester: hasEntitlement('can_access_beta_features'),
    isLocalGuide: hasRole('verifier'),
    isPoiOwner: hasEntitlement('can_manage_poi_content'),
    isModerator: hasRole('spot_admin'),
    isAdmin: isSuperAdmin,
    isSuperAdmin,
    roles,
  };
}
